/**
 * Absolute `/api/v1/customer-account/...` JSON helper (identity-only safe).
 * Real callers: Wave E orders (`ordersApi`), Wave E payments/cancel (`accountApi`),
 * and platform pickup resolve-code (`findOrderApi`). Hub `/me` goes through
 * {@link fetchPlatformMe} / `fetchPlatformAccountMe` — not this helper.
 * Leave / activate-staged still call raw {@link stormAwareFetch}.
 *
 * Spec A cookie-first (AppDataProvider twin): always credentials include + zero
 * Authorization. `accessToken` is ignored so callers cannot accidentally send
 * memory Bearer + credentials:'omit' (drops HttpOnly jar → 401 while chrome
 * still looks signed-in from prior `/me` memory).
 *
 * G-ORDERS / Spec A dying-access: on 401, run the same
 * {@link handleUnauthorizedForPath} recovery as ApiClient (force rotate when a
 * React session remains after access Max-Age deleted the jar cookie), then
 * apply AppDataProvider twin end/suspend side effects and retry once on
 * `'retry'`. Map storm/network failures to {@link ApiError} so Objednávky
 * shows auth vs retry UX correctly.
 */
import { readRequestId, setClientCorrelationId } from 'pi-kiosk-shared/logging';
import { setSentryCorrelationId } from 'pi-kiosk-shared/sentry';
import {
  ApiError,
  shouldAttemptCustomerApiRefreshOn401,
} from '../../shared/api/ApiClient.js';
import { customerInvalidJsonApiError } from '../../shared/errors/customerApiParseErrors.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { CustomerOutboundFairQueueOverflowError } from '../../shared/http/customerOutboundFairQueue.js';
import { resolveApiErrorFromHttpResponse } from '../../shared/http/resolveApiErrorFromHttpResponse.js';
import {
  HttpStormLatchedError,
  stormAwareFetch,
} from '../../shared/http/stormAwareFetch.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { endCustomerSessionGlobal } from '../../shared/session/customerAuthSync.js';
import { handleUnauthorizedForPath } from '../../shared/session/sessionRefresh.js';
import { resolveCustomerCredentialSend } from '../../shared/session/resolveCustomerCredentialSend.js';
import {
  customerAuthCsrfHeaders,
  shouldAttachCustomerAuthCsrf,
} from '../../shared/session/customerAuthCsrf.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

function platformAccountUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}${path}`;
}

/** Same-origin empty apiBaseUrl → relative `/api/...` (BAN `new URL` without base in Node). */
function buildPlatformAccountRequestUrl(
  apiBaseUrl: string,
  path: string,
  query?: Record<string, string | number | undefined>,
): string {
  let url = platformAccountUrl(apiBaseUrl, path);
  if (query !== undefined) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    }
    const qs = params.toString();
    if (qs.length > 0) {
      url += `?${qs}`;
    }
  }
  return url;
}

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return true;
  }
  return err instanceof Error && err.name === 'AbortError';
}

function rethrowStormOrNetwork(err: unknown): never {
  if (err instanceof ApiError) {
    throw err;
  }
  // Preserve AbortError identity (do not remap to NETWORK_ERROR status 0).
  // Guest /me paths must not unrecord ceiling on abort (G1/G3); see meAttemptCeiling.
  if (isAbortError(err)) {
    throw err;
  }
  // G2 — fair-queue overflow parity with ApiClient.mapOutboundScheduleError
  // (status 429, not NETWORK_ERROR status 0 → Objednávky connection UX).
  // Overflow Error.message stays technical for logs; ApiError.message is public.
  if (err instanceof CustomerOutboundFairQueueOverflowError) {
    throw new ApiError(
      resolvePublicApiFailureMessage({
        status: 429,
        code: err.code,
        rawMessage: err.message,
      }),
      429,
      err.code,
      undefined,
      err.retryAfterMs,
    );
  }
  if (err instanceof HttpStormLatchedError) {
    throw new ApiError(
      resolvePublicApiFailureMessage({
        status: 429,
        code: 'HTTP_STORM_DETECTED',
        rawMessage: err.message,
      }),
      429,
      'HTTP_STORM_DETECTED',
      undefined,
      err.retryAfterMs,
    );
  }
  throw new ApiError(
    resolvePublicApiFailureMessage({
      status: 0,
      code: 'NETWORK_ERROR',
      rawMessage: err instanceof Error ? err.message : undefined,
    }),
    0,
    'NETWORK_ERROR',
  );
}

async function fetchPlatformAccountJsonOnce<T>(input: {
  apiBaseUrl: string;
  path: string;
  method: 'GET' | 'POST' | 'PUT';
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  cache?: RequestCache;
}): Promise<T> {
  const credentialSend = resolveCustomerCredentialSend({
    bearerToken: null,
  });
  const headers: Record<string, string> = platformJsonHeaders(
    input.body !== undefined ? { 'Content-Type': 'application/json' } : {},
  );
  if (credentialSend.authorizationHeader !== undefined) {
    headers.Authorization = credentialSend.authorizationHeader;
  }
  if (shouldAttachCustomerAuthCsrf(input.method, input.path)) {
    Object.assign(headers, customerAuthCsrfHeaders());
  }

  const url = buildPlatformAccountRequestUrl(
    input.apiBaseUrl,
    input.path,
    input.query,
  );

  let response: Response;
  try {
    response = await stormAwareFetch(url, {
      method: input.method,
      headers,
      credentials: credentialSend.credentials,
      signal: input.signal,
      ...(input.cache !== undefined ? { cache: input.cache } : {}),
      ...(input.body !== undefined ? { body: JSON.stringify(input.body) } : {}),
    });
  } catch (err) {
    rethrowStormOrNetwork(err);
  }

  const headerRequestId = readRequestId(response);
  if (headerRequestId !== undefined) {
    setClientCorrelationId(headerRequestId);
    setSentryCorrelationId(headerRequestId);
  }

  let body: SuccessEnvelope<T> | ErrorEnvelope;
  try {
    body = (await response.json()) as SuccessEnvelope<T> | ErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  const bodyRequestIdCandidate =
    body !== null && typeof body === 'object' && 'requestId' in body
      ? (body as { requestId?: unknown }).requestId
      : undefined;
  const bodyRequestId =
    typeof bodyRequestIdCandidate === 'string' ? bodyRequestIdCandidate : undefined;
  const correlationId = headerRequestId ?? bodyRequestId;
  if (correlationId !== undefined) {
    setClientCorrelationId(correlationId);
    setSentryCorrelationId(correlationId);
  }

  if (!response.ok || body.success !== true) {
    const resolved = resolveApiErrorFromHttpResponse({
      response,
      body,
    });
    throw new ApiError(
      resolved.message,
      response.status,
      resolved.code,
      body,
      resolved.retryAfterMs,
    );
  }

  return body.data;
}

export async function fetchPlatformAccountJson<T>(input: {
  apiBaseUrl: string;
  path: string;
  method?: 'GET' | 'POST' | 'PUT';
  /**
   * @deprecated Spec A — ignored; cookie jar is always used (parity with
   * AppDataProvider `shouldAttachCustomerBearer: () => false`).
   */
  accessToken?: string | null;
  signal?: AbortSignal;
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  /** Fetch cache mode (memberships branding — `no-store` /orgs parity). */
  cache?: RequestCache;
}): Promise<T> {
  void input.accessToken;
  const method = input.method ?? 'GET';
  const onceInput = {
    apiBaseUrl: input.apiBaseUrl,
    path: input.path,
    method,
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
    ...(input.query !== undefined ? { query: input.query } : {}),
    ...(input.body !== undefined ? { body: input.body } : {}),
    ...(input.cache !== undefined ? { cache: input.cache } : {}),
  };

  try {
    return await fetchPlatformAccountJsonOnce<T>(onceInput);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) {
      throw err;
    }
    if (!shouldAttemptCustomerApiRefreshOn401(input.path)) {
      throw err;
    }
    const action = await handleUnauthorizedForPath(
      loadRuntimeConfig(),
      input.path,
      err.code,
      err.message,
    );
    if (action === 'end') {
      await endCustomerSessionGlobal({
        broadcast: true,
        callApi: false,
        reason: 'revoked',
      });
    }
    if (action === 'retry') {
      return fetchPlatformAccountJsonOnce<T>(onceInput);
    }
    throw err;
  }
}
